import React, { useState, useCallback, useEffect } from 'react';

interface PhoneInputProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter phone number',
  className = '',
  id,
  name,
  disabled = false
}) => {
  const [displayValue, setDisplayValue] = useState(() => {
    // Format the initial value if it exists
    if (value) {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length <= 3) return `(${cleaned}`;
      if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    return '';
  });

  // Format phone number for display
  const formatPhoneNumber = useCallback((input: string): string => {
    // Remove all non-digit characters
    const cleaned = input.replace(/\D/g, '');
    
    // Format based on length
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }, []);

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input);
    
    setDisplayValue(formatted);
    
    // Extract only digits for the actual value
    const digitsOnly = input.replace(/\D/g, '');
    onChange(digitsOnly);
  }, [formatPhoneNumber, onChange]);

  // Handle backspace to remove formatting characters
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      const currentValue = displayValue;
      const cursorPosition = e.currentTarget.selectionStart || 0;
      
      // If cursor is on a formatting character, move it back
      if (currentValue[cursorPosition - 1] === ' ' || 
          currentValue[cursorPosition - 1] === '-' || 
          currentValue[cursorPosition - 1] === ')') {
        e.preventDefault();
        const newPosition = cursorPosition - 1;
        e.currentTarget.setSelectionRange(newPosition, newPosition);
      }
    }
  }, [displayValue]);

  // Update display value when value prop changes
  useEffect(() => {
    if (value !== displayValue.replace(/\D/g, '')) {
      const formatted = formatPhoneNumber(value);
      setDisplayValue(formatted);
    }
  }, [value, displayValue, formatPhoneNumber]);

  return (
    <input
      type="tel"
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={`mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${className}`}
      id={id}
      name={name}
      disabled={disabled}
      maxLength={14} // (XXX) XXX-XXXX
    />
  );
};

export default PhoneInput;
